import { ReactNode, useEffect, useState, useRef } from 'react';
import AuthContext from './AuthContext';
import { useConfig } from '../hooks/useConfig';
import { IUserLogin, IUserLoginTokens, IVerifyCode, IRegisterUser, IPasswordReset, IAcceptInvitation, ICompleteRegistration } from './types';
import { useLazyQuery, useMutation } from '@apollo/client';
import { AuthenticatedUser, MutationAcceptInvitationArgs, MutationAcceptTermsArgs, MutationCompleteRegistrationArgs, MutationForgotPasswordArgs, MutationForgotPasswordResetArgs, MutationLoginArgs, MutationLoginSessionArgs, MutationRefreshAccessTokenArgs, MutationRegisterArgs, MutationResendCodeArgs, MutationVerifyCodeArgs, QueryValidateInvitationStatusArgs, InvitationStatus, Seller, SetSellerInput, User } from '@shopify-multiseller/graphql/partner/types';
import { LOGIN_MUTATION, LOGIN_SESSION_MUTATION, REFRESH_ACCESS_TOKEN_MUTATION, LOGOUT_MUTATION, REGISTER_MUTATION, VERIFY_CODE_MUTATION, RESEND_CODE_MUTATION, FORGOT_PASSWORD_MUTATION, FORGOT_PASSWORD_RESET_MUTATION, ACCEPT_INVITATION_MUTATION, VALIDATE_INVITATION_QUERY, COMPLETE_REGISTRATION_MUTATION, ACCEPT_TERMS_MUTATION, ME_QUERY, SELLER_QUERY, UPDATE_SELLER_MUTATION } from './queries';
import { useAuthenticatedUser } from './AuthenticatedUserProvider';
import { StorefrontConfigAuth } from 'config-types';
import { useSellerRefresh } from '../hooks/useSellerRefresh';

type AuthProviderProps = {
  children?: ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const providerConfig = useConfig();
  const currentAuthConfig: StorefrontConfigAuth | undefined = providerConfig?.shopConfig?.auth;

  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);

  if (currentAuthConfig === undefined) throw new Error("AuthProvider: Configuration settings have not been defined.");

  const authenticatedUser = useAuthenticatedUser();

  const isLoggedIn = authenticatedUser.user != null;
  const isRegistered: boolean = authenticatedUser.user ? authenticatedUser.user?.isRegistered : false;

  const [loginMutation] = useMutation<{
    login: AuthenticatedUser
  }, MutationLoginArgs>(LOGIN_MUTATION);

  const [loginSessionMutation] = useMutation<{
    loginSession: AuthenticatedUser
  }, MutationLoginSessionArgs>(LOGIN_SESSION_MUTATION);

  const [refreshAccessTokenMutation] = useMutation<{
    refreshAccessToken: AuthenticatedUser
  }, MutationRefreshAccessTokenArgs>(REFRESH_ACCESS_TOKEN_MUTATION);

  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  const [registerMutation] = useMutation<{
    register: AuthenticatedUser
  }, MutationRegisterArgs>(REGISTER_MUTATION);

  const [verifyCodeMutation] = useMutation<{
    verifyCode: User
  }, MutationVerifyCodeArgs>(VERIFY_CODE_MUTATION);

  const [resendCodeMutation] = useMutation<{
    resendCode: boolean
  }, MutationResendCodeArgs>(RESEND_CODE_MUTATION);

  const [forgotPasswordMutation] = useMutation<{
    forgotPassword: boolean
  }, MutationForgotPasswordArgs>(FORGOT_PASSWORD_MUTATION);

  const [forgotPasswordResetMutation] = useMutation<{
    forgotPasswordReset: boolean
  }, MutationForgotPasswordResetArgs>(FORGOT_PASSWORD_RESET_MUTATION);

  const [acceptInvitationMutation] = useMutation<{
    acceptInvitation: AuthenticatedUser
  }, MutationAcceptInvitationArgs>(ACCEPT_INVITATION_MUTATION);

  const [validateInvitationQuery] = useLazyQuery<{
    validateInvitationStatus: { status: InvitationStatus }
  }, QueryValidateInvitationStatusArgs>(VALIDATE_INVITATION_QUERY);

  const [acceptTermsMutation] = useMutation<{
    acceptTerms: Seller
  }, MutationAcceptTermsArgs>(ACCEPT_TERMS_MUTATION);

  const [completeRegistrationMutation] = useMutation<{
    completeRegistration: User
  }, MutationCompleteRegistrationArgs>(COMPLETE_REGISTRATION_MUTATION);

  const [meQuery] = useLazyQuery<{
    me: User
  }, {}>(ME_QUERY);

  const [_getSeller] = useLazyQuery<{
    seller: Seller
  }, {}>(SELLER_QUERY);

  const [updateSellerMutation] = useMutation<{ updateSeller: Seller }, { input: SetSellerInput }>(UPDATE_SELLER_MUTATION);

  const { refreshSeller } = useSellerRefresh({ skipInitialRefresh: true });

  // Track if we've done the initial refresh
  const hasRefreshed = useRef(false);

  useEffect(() => {
    if (isLoggedIn && !hasRefreshed.current) {
      refreshSeller();
      hasRefreshed.current = true;
    } else if (!isLoggedIn) {
      // Reset the flag when logged out
      hasRefreshed.current = false;
    }
  }, [isLoggedIn]);

  const updateSeller = async (input: SetSellerInput) => {
    const { data } = await updateSellerMutation({ variables: { input } });
    if (data?.updateSeller && authenticatedUser.user) {
      const updatedUser = { ...authenticatedUser.user, seller: data.updateSeller };
      authenticatedUser.updateUser(updatedUser);
      return data.updateSeller;
    }
    return undefined;
  };

  const acceptInvitation = async (value: IAcceptInvitation): Promise<void> => {
    await acceptInvitationMutation({
      variables: {
        invitationCode: value.invitationCode,
        password: value.password
      }
    }).then(({ data }) => {
      if (data) {
        sessionStorage.removeItem("invitationCode");
        authenticatedUser.update(data.acceptInvitation.user, data.acceptInvitation.tokens);
      } else {
        throw new Error("User not found");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  }

  const login = async (value: IUserLogin): Promise<void> => {
    resetErrorMessage();
    setLoggingIn(true);
    await loginMutation({
      variables: {
        value: {
          username: value.username,
          password: value.password
        }
      }
    }).then(({ data }) => {
      if (data) {
        authenticatedUser.update(data.login.user, data.login.tokens);
        // wait for 1 second before setting loggingIn to false
        setTimeout(() => {
          setLoggingIn(false);
        }, 1000);
      } else {
        setLoggingIn(false);
        throw new Error("User login failed");
      }
    }).catch((error: any) => {
      setLoggingIn(false);
      setErrorMessage(error.message);
      throw new Error(error.message);
    });
  };

  const refreshUser = async (): Promise<void> => {
    await meQuery().then(({ data }) => {
      if (data) {
        authenticatedUser.updateUser(data.me);
      } else {
        throw new Error("Refresh user failed");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  }

  const loginSession = async (value: IUserLoginTokens): Promise<void> => {
    await loginSessionMutation({ variables: { value } }).then(({ data }) => {
      if (data) {
        authenticatedUser.update(data.loginSession.user, data.loginSession.tokens);
      } else {
        throw new Error("User not found");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const refreshAccessToken = async (): Promise<void> => {
    if (authenticatedUser.user == null || authenticatedUser.tokens == null) throw new Error("User not logged in");
    const username = authenticatedUser.user.username;
    const refreshToken = authenticatedUser.tokens.refreshToken;
    await refreshAccessTokenMutation({ variables: { username, refreshToken } }).then(({ data }) => {
      if (data) {
        authenticatedUser.update(data.refreshAccessToken.user, data.refreshAccessToken.tokens);
      } else {
        throw new Error("User not found");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const logout = async (): Promise<void> => {
    // Get shopId before we destroy the authenticated user
    setLoggingOut(true);
    const shopId = providerConfig?.shopConfig?.auth?.shopId;

    if (!shopId) {
      throw new Error("No shop ID available for logout");
    }

    try {
      const { data, errors = [] } = await logoutMutation();

      if (errors.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.logout) {
        throw new Error("Logout mutation did not return a valid response.");
      }

      // Only destroy the authenticated user after successful logout
      authenticatedUser.destroy();
    } catch (error: any) {
      console.error("Logout error:", error);
      throw error;
    } finally {
      setLoggingOut(false);
    }
  };

  // These must await their mutation: callers show success/error UI off the
  // returned promise, and a fire-and-forget call resolves before the server
  // answers (success screens for failed requests, errors lost as unhandled
  // rejections).
  const forgotPassword = async (username: string): Promise<void> => {
    await forgotPasswordMutation({ variables: { username } }).then(() => {
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const verifyCode = async (value: IVerifyCode): Promise<void> => {
    await verifyCodeMutation({ variables: { value } }).then(() => {
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const resendCode = async (username: string): Promise<void> => {
    await resendCodeMutation({ variables: { username } }).then(() => {
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const register = async (value: IRegisterUser): Promise<void> => {
    await registerMutation({ variables: { value } }).then(({ data }) => {
      if (data?.register) {
        authenticatedUser.update(data.register.user, data.register.tokens);
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  };

  const forgotPasswordReset = async (value: IPasswordReset): Promise<void> => {
    try {
      await forgotPasswordResetMutation({ variables: { value } });
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const validateInvitation = async (invitationCode: string): Promise<InvitationStatus> => {
    const response = await validateInvitationQuery({
      variables: { invitationCode },
    });
    const status = response.data?.validateInvitationStatus.status ?? InvitationStatus.Unknown;
    // Only a usable invitation is worth carrying forward to the acceptance
    // screen; the others send the seller to a result card instead.
    if (status === InvitationStatus.Valid) {
      sessionStorage.setItem("invitationCode", invitationCode);
    }
    return status;
  }

  const acceptTerms = async (termsVersion: string): Promise<void> => {
    await acceptTermsMutation({ variables: { version: termsVersion } }).then(({ data }) => {
      if (data) {
        if (!authenticatedUser.user) throw new Error("Authenticated user not set.");
        authenticatedUser.updateUser({ ...authenticatedUser.user, seller: data.acceptTerms });
      } else {
        throw new Error("User not found");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  }

  const completeRegistration = async (value: ICompleteRegistration): Promise<void> => {
    await completeRegistrationMutation({
      variables: {
        completeRegistration: {
          companyName: value.companyName,
          phoneNumber: value.phone,
          firstName: value.firstName,
          lastName: value.lastName,
          countryCode: value.countryCode
        }
      }
    }).then(({ data }) => {
      if (data) {
        authenticatedUser.updateUser(data.completeRegistration);
      } else {
        throw new Error("User not found");
      }
    }).catch((error: any) => {
      throw new Error(error.message);
    });
  }

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetErrorMessage = (): void => {
    setErrorMessage(null);
  }


  return (
    <AuthContext.Provider
      value={{
        updateSeller,
        refreshSeller,
        loggingOut,
        errorMessage,
        resetErrorMessage,
        loggingIn,
        user: authenticatedUser.user,
        tokens: authenticatedUser.tokens,
        login,
        loginSession,
        logout,
        verifyCode,
        forgotPassword,
        forgotPasswordReset,
        register,
        resendCode,
        isLoggedIn,
        isRegistered,
        refreshAccessToken,
        acceptInvitation,
        validateInvitation,
        acceptTerms,
        refreshUser,
        completeRegistration,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
